import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import FormLayout from '../../../components/Common/FormLayout';

interface ChecklistFormData {
  veiculo_id: number | null;
  veiculo_placa: string;
  equipamento_id: number | null;
  equipamento_tag: string;
  equipamento_nome: string;
  motorista_id: number | null; // Permitir null - sem motorista selecionado por padrão
  motorista_nome: string;
  tipo: string;
  status: 'pendente' | 'concluido' | 'reprovado';
  data_realizacao: string;
  observacoes?: string;
  quilometragem: number;
  itens: ChecklistItem[];
}

interface ChecklistItem {
  id: number;
  originalItemId?: number; // ID do item padrão original
  descricao: string;
  categoria: string;
  tipo?: string; // Tipo do item (booleano, texto, numero, opcoes)
  opcoes?: string[]; // Opções disponíveis para tipo 'opcoes'
  obrigatorio: boolean;
  ordem?: number; // Ordem de exibição do item
  valor: 'aprovado' | 'reprovado' | 'pendente';
  observacao?: string;
}

interface Vehicle {
  id: number;
  placa: string;
  modelo: string;
  quilometragem_atual: number;
}

interface Driver {
  id: number;
  nome: string;
  cpf: string;
}

interface Equipment {
  id: number;
  tag: string;
  nome: string;
  tipo: string;
}

const ChecklistCreate: React.FC = () => {
  const navigate = useNavigate();
  // Função para obter data/hora local no formato ISO sem conversão UTC
  const getLocalISOString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const localDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    console.log('🕐 Horário local gerado:', localDateTime);
    console.log('🌍 Horário UTC (antigo):', now.toISOString());
    return localDateTime;
  };

  const [checklistEntityType, setChecklistEntityType] = useState<'veiculo' | 'equipamento'>('veiculo');
  const [formData, setFormData] = useState<ChecklistFormData>({
    veiculo_id: null,
    veiculo_placa: '',
    equipamento_id: null,
    equipamento_tag: '',
    equipamento_nome: '',
    motorista_id: null, // Remover valor padrão - deixar sem motorista selecionado
    motorista_nome: '',
    tipo: 'pre_viagem',
    status: 'pendente',
    data_realizacao: getLocalISOString(),
    quilometragem: 0,
    itens: []
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [defaultItems, setDefaultItems] = useState<any[]>([]);
  const [isLoadingDefaultItems, setIsLoadingDefaultItems] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);

      // Carregar veículos, equipamentos e motoristas
      const [vehiclesResponse, equipmentsResponse, driversResponse] = await Promise.all([
        apiService.getVehicles({ page: 1, limit: 100 }),
        apiService.getEquipments({ page: 1, limit: 100 }),
        apiService.getDrivers({ page: 1, limit: 100 })
      ]);

      if (vehiclesResponse.success && vehiclesResponse.data) {
        setVehicles(vehiclesResponse.data || []);
      }

      if (equipmentsResponse.success && equipmentsResponse.data) {
        // A API de equipamentos retorna { equipamentos: [], pagination: {} }
        const equipmentsData = (equipmentsResponse.data as any).equipamentos || equipmentsResponse.data;
        setEquipments(Array.isArray(equipmentsData) ? equipmentsData : []);
      }

      if (driversResponse.success && driversResponse.data) {
        setDrivers(driversResponse.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      utils.showNotification('Erro ao carregar dados para criar checklist', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'veiculo_id') {
      const vehicleId = parseInt(value);
      const vehicle = vehicles.find(v => v.id === vehicleId);
      setFormData(prev => ({
        ...prev,
        veiculo_id: vehicleId,
        veiculo_placa: vehicle?.placa || '',
        quilometragem: vehicle?.quilometragem_atual || 0
      }));
    } else if (name === 'equipamento_id') {
      const equipmentId = parseInt(value);
      const equipment = equipments.find(e => e.id === equipmentId);
      setFormData(prev => ({
        ...prev,
        equipamento_id: equipmentId,
        equipamento_tag: equipment?.tag || '',
        equipamento_nome: equipment?.nome || ''
      }));
    } else if (name === 'motorista_id') {
      const driverId = parseInt(value);
      const driver = drivers.find(d => d.id === driverId);
      setFormData(prev => ({
        ...prev,
        motorista_id: driverId,
        motorista_nome: driver?.nome || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEntityTypeChange = (type: 'veiculo' | 'equipamento') => {
    setChecklistEntityType(type);
    // Resetar campos relacionados ao tipo anterior
    if (type === 'veiculo') {
      setFormData(prev => ({
        ...prev,
        equipamento_id: null,
        equipamento_tag: '',
        equipamento_nome: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        veiculo_id: null,
        veiculo_placa: '',
        quilometragem: 0
      }));
    }
  };

  const openAddItemModal = async () => {
    setShowAddItemModal(true);
    if (defaultItems.length === 0) {
      await loadDefaultItems();
    }
  };

  const loadDefaultItems = async () => {
    try {
      setIsLoadingDefaultItems(true);
      const response = await apiService.getChecklistItemsDefault() as ApiResponse<any>;

      if (response.success && response.data) {
        setDefaultItems(response.data);
      } else {
        utils.showNotification('Erro ao carregar itens padrão', { type: 'error' });
      }
    } catch (error) {
      console.error('Erro ao carregar itens padrão:', error);
      utils.showNotification('Erro ao carregar itens padrão', { type: 'error' });
    } finally {
      setIsLoadingDefaultItems(false);
    }
  };

  const addSelectedItems = (selectedItems: ChecklistItem[]) => {
    const newChecklistItems: ChecklistItem[] = selectedItems.map(item => ({
      id: -Date.now() - Math.random(), // IDs únicos negativos para novos itens
      originalItemId: item.id, // Rastreia o ID do item padrão original
      descricao: item.descricao,
      categoria: item.categoria,
      tipo: item.tipo, // Copia o tipo do item padrão
      opcoes: item.opcoes, // Copia as opções disponíveis
      obrigatorio: item.obrigatorio,
      ordem: item.ordem, // Copia a ordem do item padrão
      valor: 'pendente' as const, // ✅ CORREÇÃO: Itens novos começam sempre como pendente
      observacao: '' // Inicia sempre em branco para preenchimento manual
    }));

    setFormData(prev => prev ? { ...prev, itens: [...prev.itens, ...newChecklistItems] } : prev);
    // Modal permanece aberto para permitir seleção de mais itens
    // setShowAddItemModal(false);
  };

  const removeItem = (itemId: number) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.filter(item => item.id !== itemId)
    }));
  };

  const handleItemChange = (itemId: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    try {
      setIsSaving(true);

      // Validar campos obrigatórios
      const hasVehicle = checklistEntityType === 'veiculo' && formData.veiculo_id;
      const hasEquipment = checklistEntityType === 'equipamento' && formData.equipamento_id;

      if ((!hasVehicle && !hasEquipment) || !formData.motorista_id || !formData.tipo || !formData.data_realizacao) {
        utils.showNotification('Preencha todos os campos obrigatórios', { type: 'error' });
        return;
      }

      // Validar observações obrigatórias para itens reprovados
      const missingObservations = formData.itens
        .filter(item => item.valor === 'reprovado' && (!item.observacao || item.observacao.trim() === ''))
        .map(item => item.descricao);

      if (missingObservations.length > 0) {
        utils.showNotification(
          `Itens reprovados devem ter observações: ${missingObservations.join(', ')}`,
          { type: 'error' }
        );
        return;
      }

      // Converter dados para o formato da API
      const apiData: any = {
        motorista_id: formData.motorista_id,
        tipo: formData.tipo,
        status: formData.status,
        data_realizacao: formData.data_realizacao,
        observacoes: formData.observacoes,
        itens: formData.itens.map(item => ({
          categoria: item.categoria,
          descricao: item.descricao,
          tipo: (item.tipo as string) || 'booleano',
          opcoes: item.opcoes || null,
          obrigatorio: item.obrigatorio,
          ordem: item.ordem || 0,
          valor: item.valor,
          observacao: item.observacao
        }))
      };

      // Adicionar veiculo_id ou equipamento_id baseado no tipo selecionado
      if (checklistEntityType === 'veiculo') {
        apiData.veiculo_id = formData.veiculo_id;
        apiData.odometro = formData.quilometragem;
      } else {
        apiData.equipamento_id = formData.equipamento_id;
        // Para equipamentos, odometro/horimetro é opcional
        if (formData.quilometragem > 0) {
          apiData.odometro = formData.quilometragem;
        }
      }

      const response = await apiService.createChecklist(apiData) as ApiResponse<any>;

      if (response.success && response.data) {
        utils.showNotification('Checklist criado com sucesso!', { type: 'success' });
        navigate('/checklists');
      } else {
        throw new Error(response.message || 'Erro ao criar checklist');
      }
    } catch (error) {
      console.error('Erro ao criar checklist:', error);
      utils.showNotification('Erro ao criar checklist', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '';
    // Se já está no formato correto (YYYY-MM-DDTHH:mm), retorna direto
    if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
      return dateString.slice(0, 16);
    }
    // Caso contrário, converte mantendo o horário local
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTime = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    // Mantém o formato ISO local sem conversão para UTC
    if (!dateTimeString.includes('T')) {
      return dateTimeString;
    }
    // Garante que tenha segundos
    if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      return dateTimeString + ':00';
    }
    return dateTimeString;
  };

  if (isLoading) {
    return (
      <FormLayout
        title="Novo Checklist"
        subtitle="Criação de Checklist"
        isLoading={true}
        onCancel={() => navigate('/checklists')}
        showActions={false}
      >
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando dados...</p>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Novo Checklist"
      subtitle="Criar novo checklist"
      isLoading={isSaving}
      onCancel={() => navigate('/checklists')}
      showActions={false}
    >
      <form onSubmit={handleSave}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>

          {/* Seleção de Tipo de Checklist */}
          <div className="form-group" style={{ marginBottom: '24px' }}>
            <label className="form-label" style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block' }}>
              Tipo de Checklist *
            </label>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px 12px', border: checklistEntityType === 'veiculo' ? '2px solid #04C64F' : '2px solid #dee2e6', borderRadius: '8px', backgroundColor: checklistEntityType === 'veiculo' ? '#f0fdf4' : 'white', transition: 'all 0.3s ease', whiteSpace: 'nowrap' }}>
                <input
                  type="radio"
                  name="checklistEntityType"
                  value="veiculo"
                  checked={checklistEntityType === 'veiculo'}
                  onChange={() => handleEntityTypeChange('veiculo')}
                  style={{ marginRight: '6px', cursor: 'pointer' }}
                />
                <i className="fas fa-truck" style={{ marginRight: '6px', color: '#04C64F' }}></i>
                <span style={{ fontWeight: '500' }}>Checklist de Veículo</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px 12px', border: checklistEntityType === 'equipamento' ? '2px solid #04C64F' : '2px solid #dee2e6', borderRadius: '8px', backgroundColor: checklistEntityType === 'equipamento' ? '#f0fdf4' : 'white', transition: 'all 0.3s ease', whiteSpace: 'nowrap' }}>
                <input
                  type="radio"
                  name="checklistEntityType"
                  value="equipamento"
                  checked={checklistEntityType === 'equipamento'}
                  onChange={() => handleEntityTypeChange('equipamento')}
                  style={{ marginRight: '6px', cursor: 'pointer' }}
                />
                <i className="fas fa-tools" style={{ marginRight: '6px', color: '#04C64F' }}></i>
                <span style={{ fontWeight: '500' }}>Checklist de Equipamento</span>
              </label>
            </div>
          </div>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {/* Campo condicional: Veículo ou Equipamento */}
            {checklistEntityType === 'veiculo' ? (
              <div className="form-group">
                <label className="form-label">Veículo *</label>
                <select
                  name="veiculo_id"
                  value={formData.veiculo_id || ''}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="">Selecione um veículo</option>
                  {vehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.placa} - {vehicle.modelo}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Equipamento *</label>
                <select
                  name="equipamento_id"
                  value={formData.equipamento_id || ''}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="">Selecione um equipamento</option>
                  {equipments.map(equipment => (
                    <option key={equipment.id} value={equipment.id}>
                      {equipment.tag} - {equipment.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Motorista *</label>
              <select
                name="motorista_id"
                value={formData.motorista_id || ''} // Tratamento para null -> string vazia
                onChange={handleInputChange}
                className="form-control"
                required
              >
                <option value="">Selecione um motorista</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.nome} - {driver.cpf}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Checklist *</label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                className="form-control"
                required
              >
                <option value="pre_viagem">Pré-viagem</option>
                <option value="pos_viagem">Pós-viagem</option>
                <option value="diario">Diário</option>
                <option value="periodico">Periódico</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Data e Hora *</label>
              <input
                type="datetime-local"
                name="data_realizacao"
                value={formatDateTime(formData.data_realizacao)}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  data_realizacao: parseDateTime(e.target.value)
                }))}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {checklistEntityType === 'veiculo' ? 'Quilometragem *' : 'Quilometragem/Horímetro'}
              </label>
              <input
                type="number"
                name="quilometragem"
                value={formData.quilometragem}
                onChange={handleInputChange}
                className="form-control"
                min="0"
                step="1"
                required={checklistEntityType === 'veiculo'}
                placeholder={checklistEntityType === 'equipamento' ? 'Opcional' : ''}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
                required
              >
                <option value="pendente">Pendente</option>
                <option value="concluido">Concluído</option>
                <option value="reprovado">Reprovado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-group">
              <label className="form-label">Observações Gerais</label>
              <textarea
                name="observacoes"
                value={formData.observacoes || ''}
                onChange={handleInputChange}
                className="form-control"
                rows={4}
                placeholder="Digite observações gerais sobre o checklist..."
              />
            </div>
          </div>
        </div>

        {/* Itens do Checklist */}
        <div style={{ marginBottom: '30px' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px', margin: 0 }}>
              Itens do Checklist ({formData.itens.length})
            </h3>

            <button
              type="button"
              onClick={openAddItemModal}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                backgroundColor: '#04C64F',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(4, 198, 79, 0.3)',
                minWidth: '140px',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-plus"></i>
              Adicionar Item
            </button>
          </div>

          {/* Botão adicional quando não há itens */}
          {formData.itens.length === 0 && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={openAddItemModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#04C64F',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(4, 198, 79, 0.3)',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-plus"></i> Adicionar Itens do Checklist
              </button>
            </div>
          )}

          {formData.itens.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '50px 20px',
              backgroundColor: '#f8f9fa',
              border: '2px dashed #dee2e6',
              borderRadius: '8px',
              color: '#6c757d'
            }}>
              <i className="fas fa-clipboard-list" style={{ fontSize: '3rem', marginBottom: '20px', color: '#adb5bd' }}></i>
              <h4 style={{ marginBottom: '10px', color: '#495057' }}>Nenhum item adicionado</h4>
              <p>Adicione itens do checklist padrão para começar a avaliação {checklistEntityType === 'veiculo' ? 'do veículo' : 'do equipamento'}.</p>
              <button
                type="button"
                onClick={openAddItemModal}
                style={{
                  marginTop: '15px',
                  padding: '12px 24px',
                  backgroundColor: '#04C64F',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(4, 198, 79, 0.3)'
                }}
              >
                <i className="fas fa-plus"></i> Adicionar Itens
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
              {formData.itens.map(item => (
                <div key={item.id} className="card" style={{ padding: '16px', paddingTop: '50px', position: 'relative' }}>
                  {/* Botão de remover - no topo direito do card */}
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                    title="Remover item"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#c0392b';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#e74c3c';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>

                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      {item.descricao}
                      {item.obrigatorio && (
                        <span style={{ color: '#e74c3c', marginLeft: '8px' }}>*</span>
                      )}
                    </label>
                    <div style={{ color: '#7f8c8d', fontSize: '0.8rem', marginBottom: '12px' }}>
                      Categoria: {item.categoria}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Status
                      </label>
                      <select
                        value={item.valor}
                        onChange={(e) => handleItemChange(item.id, 'valor', e.target.value)}
                        className="form-control"
                      >
                        <option value="pendente">Pendente</option>
                        <option value="aprovado">Aprovado</option>
                        <option value="reprovado">Reprovado</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Observação {item.valor === 'reprovado' && <span style={{ color: '#e74c3c' }}>*</span>}
                      </label>
                      <textarea
                        value={item.observacao || ''}
                        onChange={(e) => handleItemChange(item.id, 'observacao', e.target.value)}
                        className="form-control"
                        rows={2}
                        placeholder="Observação específica para este item..."
                        required={item.valor === 'reprovado'}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal para adicionar itens padrão */}
        {showAddItemModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ margin: 0, color: '#2c3e50' }}>Adicionar Itens do Checklist</h3>
                <button
                  onClick={() => setShowAddItemModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#7f8c8d'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                padding: '20px',
                flex: 1,
                overflowY: 'auto'
              }}>
                {isLoadingDefaultItems ? (
                  <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div className="loading-spinner"></div>
                    <p>Carregando itens padrão...</p>
                  </div>
                ) : defaultItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d' }}>
                    <i className="fas fa-info-circle" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                    <p>Nenhum item padrão disponível</p>
                  </div>
                ) : (
                <ChecklistItemSelector
                  items={defaultItems}
                  existingItemIds={formData ? formData.itens.map(item => item.originalItemId || item.id).filter(id => id > 0) : []}
                  onSelectionChange={(selectedItems) => {
                    addSelectedItems(selectedItems);
                    setShowAddItemModal(false); // Fecha o modal automaticamente
                  }}
                />
                )}
              </div>

              <div style={{
                padding: '20px',
                borderTop: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}>
                <button
                  onClick={() => setShowAddItemModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/checklists')}
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="loading"></span>
                Criando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Criar Checklist
              </>
            )}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

// Componente auxiliar para seleção de itens
interface ChecklistItemSelectorProps {
  items: ChecklistItem[];
  existingItemIds?: number[];
  onSelectionChange: (selectedItems: ChecklistItem[]) => void;
}

// Check if this is outside of the component for good React practices
interface ChecklistItemSelectorProps {
  items: ChecklistItem[];
  existingItemIds?: number[];
  onSelectionChange: (selectedItems: ChecklistItem[]) => void;
}

const ChecklistItemSelector: React.FC<ChecklistItemSelectorProps> = ({ items, existingItemIds = [], onSelectionChange }) => {
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());

  // Quando existingItemIds muda, remove automaticamente os itens que já foram adicionados
  React.useEffect(() => {
    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      Array.from(newSelected).forEach(itemId => {
        if (existingItemIds.includes(itemId)) {
          newSelected.delete(itemId);
        }
      });
      return newSelected;
    });
  }, [existingItemIds]);

  const toggleItemSelection = (itemId: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleAddSelected = () => {
    const availableItems = items.filter(item => !existingItemIds.includes(item.id));
    const selected = availableItems.filter(item => selectedItems.has(item.id));
    onSelectionChange(selected);
    setSelectedItems(new Set());
  };

  const getAvailableItems = () => {
    return items.filter(item => !existingItemIds.includes(item.id));
  };

  const availableItems = getAvailableItems();

  const groupItemsByCategory = () => {
    const grouped: { [key: string]: ChecklistItem[] } = {};
    availableItems.forEach(item => {
      if (!grouped[item.categoria]) {
        grouped[item.categoria] = [];
      }
      grouped[item.categoria].push(item);
    });
    return grouped;
  };

  const groupedItems = groupItemsByCategory();
  const allSelected = availableItems.every(item => selectedItems.has(item.id));

  const handleSelectAll = () => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);

      if (allSelected) {
        // Desmarcar todos disponíveis
        availableItems.forEach(item => {
          newSet.delete(item.id);
        });
      } else {
        // Selecionar todos disponíveis
        availableItems.forEach(item => {
          newSet.add(item.id);
        });
      }

      return newSet;
    });
  };

  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleSelectAll}
          disabled={availableItems.length === 0}
          style={{
            padding: '10px 16px',
            backgroundColor: availableItems.length > 0 ? '#007bff' : '#ccc',
            border: 'none',
            borderRadius: '6px',
            color: 'white',
            cursor: availableItems.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          title={allSelected ? 'Desmarcar todos os itens exibidos' : 'Selecionar todos os itens exibidos'}
        >
          <i className={`fas fa-${allSelected ? 'check-double' : 'check-square'}`}></i>
          {allSelected ? 'Desmarcar Todos' : 'Selecionar Todos'}
          <span style={{ fontWeight: 'normal' }}>
            ({availableItems.length} itens)
          </span>
        </button>

        <button
          type="button"
          onClick={handleAddSelected}
          disabled={selectedItems.size === 0}
          style={{
            padding: '10px 20px',
            backgroundColor: selectedItems.size > 0 ? '#04C64F' : '#ccc',
            border: 'none',
            borderRadius: '6px',
            color: 'white',
            cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
            fontWeight: '600'
          }}
        >
          Adicionar Selecionados ({selectedItems.size})
        </button>
      </div>

      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {Object.entries(groupedItems).map(([categoria, categoriaItems]) => (
          <div key={categoria} style={{ marginBottom: '20px' }}>
            <h4 style={{
              marginBottom: '10px',
              color: '#2c3e50',
              borderBottom: '1px solid #ddd',
              paddingBottom: '5px'
            }}>
              {categoria} ({categoriaItems.length})
            </h4>

            <div style={{ display: 'grid', gap: '10px' }}>
              {categoriaItems.map(item => {
                const isAlreadySelected = existingItemIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItemSelection(item.id)}
                    style={{
                      padding: '12px',
                      border: selectedItems.has(item.id)
                        ? '2px solid #04C64F'
                        : isAlreadySelected
                          ? '2px solid #f39c12'
                          : '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: isAlreadySelected ? 'not-allowed' : 'pointer',
                      backgroundColor: selectedItems.has(item.id)
                        ? '#f8fffe'
                        : isAlreadySelected
                          ? '#fff3cd'
                          : 'white',
                      opacity: isAlreadySelected ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {isAlreadySelected ? (
                        <input
                          type="checkbox"
                          checked={true}
                          disabled={true}
                          style={{ cursor: 'not-allowed' }}
                        />
                      ) : (
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => {}} // Manipulado pelo onClick do container
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: '500',
                          color: isAlreadySelected ? '#f39c12' : '#2c3e50'
                        }}>
                          {item.descricao}
                          {item.obrigatorio && (
                            <span style={{ color: '#e74c3c', marginLeft: '8px' }}>*</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: '2px' }}>
                          {item.categoria}
                          {isAlreadySelected && (
                            <span style={{ color: '#f39c12', fontWeight: 'bold', marginLeft: '8px' }}>
                              Já adicionado
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChecklistCreate;
